/**
 * WizardLayout Component
 * 
 * A reusable layout template for wizard-style interfaces that provides:
 * - Full viewport card layout with native feel
 * - Sticky header with progress tracking
 * - Scrollable content area with shadow indicators
 * - Flexible footer positioning
 * - Support for headerless welcome screens
 * 
 * Originally extracted from OnboardingWizard implementation
 */

// 1. External dependencies
import React from 'react';
import { ChevronLeft, ChevronRight, Sun, Moon, Monitor } from 'lucide-react';

// 2. Workspace packages
import { Progress, Card, CardHeader, CardBody, CardFooter, ScrollShadow, Button, BrandMark, Form, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@enfomentis/ui';

// 3. Internal absolute imports
import { useTheme, type AppTheme } from '@/hooks/useTheme';

// 3. Relative imports
import { WizardLayoutProps, WizardNavigationButton } from './WizardLayout.types';

/**
 * Helper function to render a navigation button with consistent styling
 */
const renderNavigationButton = (
  config: WizardNavigationButton,
  isBack = false,
  isNext = false
): React.JSX.Element => {
  const buttonProps = {
    type: "button" as const,
    onClick: config.onClick,
    color: config.color || (isNext ? 'primary' : 'default'),
    variant: (config.variant || (isBack ? 'bordered' : 'solid')) as 'solid' | 'bordered' | 'light' | 'flat',
    size: config.size || 'md',
    ...(config.disabled !== undefined && { disabled: config.disabled }),
    ...(config.loading !== undefined && { isLoading: config.loading }),
    ...(config.className && { className: config.className }),
    ...(isBack && { startContent: <ChevronLeft className="w-4 h-4" /> }),
    ...(isNext && { endContent: <ChevronRight className="w-4 h-4" /> })
  };

  return <Button {...buttonProps}>{config.label}</Button>;
};

export const WizardLayout: React.FC<WizardLayoutProps> = ({
  children,
  currentStep,
  totalSteps,
  progress,
  stepName,
  showHeader = true,
  navigation,
  footer,
  scrollRef,
  footerAlign = 'center',
  currentProgressStep,
  totalProgressSteps,
  excludeCurrentFromProgress = false,
  title = "Setup Wizard",
  syncStatusContent,
  headerActions
}) => {
  const { currentTheme, setTheme } = useTheme();

  // Get current theme icon (same pattern as AppLayout)
  const getThemeIcon = () => {
    switch (currentTheme) {
      case 'light': return <Sun size={20} />;
      case 'dark': return <Moon size={20} />;
      case 'auto': return <Monitor size={20} />;
      default: return <Monitor size={20} />;
    }
  };

  /**
   * Render standardized navigation using TradingHub pattern
   */
  const renderNavigation = (): React.ReactNode => {
    if (!navigation) return null;

    switch (navigation.mode) {
      case 'welcome':
        // Single centered welcome button
        return navigation.welcomeButton ? (
          <div className="flex justify-center">
            {renderNavigationButton(navigation.welcomeButton, false, false)}
          </div>
        ) : null;

      case 'navigation': {
        // Back/Next pattern using TradingHub layout
        const navContent = (
          <div className="flex items-center justify-between w-full">
            {/* Left: Back button */}
            <div className="flex-1">
              {navigation.backButton ? (
                renderNavigationButton(navigation.backButton, true, false)
              ) : (
                <div /> // Empty space when no back button
              )}
            </div>

            {/* Center: Optional content */}
            <div className="flex-1 flex justify-center">
              {navigation.centerContent}
            </div>

            {/* Right: Next button */}
            <div className="flex-1 flex justify-end">
              {navigation.nextButton ? (
                renderNavigationButton(navigation.nextButton, false, true)
              ) : null}
            </div>
          </div>
        );

        // Wrap in Form if onSubmit is provided
        return navigation.onSubmit ? (
          <Form onSubmit={navigation.onSubmit}>
            {navContent}
          </Form>
        ) : navContent;
      }

      case 'none':
        // No navigation footer
        return null;

      default:
        return null;
    }
  };

  // Determine what footer content to show
  const footerContent = navigation ? renderNavigation() : footer;

  if (!showHeader) {
    // Welcome screen or headerless layout - simple full viewport with optional footer
    return (
      <div className="h-dvh bg-background transition-theme flex items-center justify-center">
        <Card
          radius="none"
          shadow="none"
          className="h-full max-h-dvh w-full max-w-5xl flex flex-col border-0"
        >
          {/* Content Area */}
          <CardBody className="flex-1 p-0 min-h-0 overflow-y-auto py-6">
            {children}
          </CardBody>

          {/* Footer - Always Visible if provided */}
          {footerContent && (
            <CardFooter className={`pb-6 pt-2 ${
              footerAlign === 'center' ? 'justify-center' :
              footerAlign === 'end' ? 'justify-end' :
              footerAlign === 'stretch' ? 'flex w-full' :
              'justify-between'
            }`}>
              {footerContent}
            </CardFooter>
          )}
        </Card>
      </div>
    );
  }

  // Standard wizard layout with header and progress
  return (
    <div className="h-dvh bg-background transition-theme flex items-center justify-center">
      <Card 
        radius="none" 
        shadow="none"
        className="h-full max-h-dvh w-full max-w-5xl flex flex-col border-0"
      >
        {/* Wizard Header with Progress */}
        <CardHeader className="flex items-start gap-3 py-2 sm:py-4">
          <BrandMark size={48} className="text-primary" />
          <div className="flex flex-col flex-1">
            <div className="flex justify-between items-start">
              <div className="flex flex-col">
                <h4 className="font-bold text-large hidden sm:block">{title}</h4>
                <h4 className="font-bold text-medium sm:hidden">{title}</h4>
                {stepName && (
                  <p className="text-small text-default-500 hidden sm:block">{stepName}</p>
                )}
              </div>
              <div className="flex items-center gap-1">
                {headerActions}
                {syncStatusContent}
                <Dropdown placement="bottom-end">
                <DropdownTrigger>
                  <Button
                    variant="light"
                    size="sm"
                    isIconOnly
                    className="shrink-0"
                    title={`Theme: ${currentTheme}`}
                  >
                    {getThemeIcon()}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label="Theme selection"
                  selectionMode="single"
                  selectedKeys={[currentTheme]}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as AppTheme;
                    if (selected) setTheme(selected);
                  }}
                >
                  <DropdownItem key="light" startContent={<Sun size={16} />}>
                    Light
                  </DropdownItem>
                  <DropdownItem key="dark" startContent={<Moon size={16} />}>
                    Dark
                  </DropdownItem>
                  <DropdownItem key="auto" startContent={<Monitor size={16} />}>
                    System
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
              </div>
            </div>
            <Progress
              value={progress}
              color="primary"
              className="w-full mt-4"
              size="sm"
              aria-label="Application progress"
              valueLabel={
                excludeCurrentFromProgress
                  ? undefined // No label for excluded steps (like welcome)
                  : `Step ${currentProgressStep || currentStep} of ${totalProgressSteps || totalSteps}`
              }
              showValueLabel={!excludeCurrentFromProgress}
            />
          </div>
        </CardHeader>

        {/* Scrollable Content Area */}
        <CardBody className="flex-1 p-0 min-h-0">
          <ScrollShadow 
            ref={scrollRef}
            className="h-full"
            hideScrollBar
            size={20}
          >
            {children}
          </ScrollShadow>
        </CardBody>

        {/* Footer - Always Visible */}
        {footerContent && (
          <CardFooter className={`pb-6 pt-0 ${
            footerAlign === 'center' ? 'justify-center' :
            footerAlign === 'end' ? 'justify-end' :
            footerAlign === 'stretch' ? 'flex w-full' :
            'justify-between'
          }`}>
            {footerContent}
          </CardFooter>
        )}
      </Card>
    </div>
  );
};